import CollectionsIcon from '@mui/icons-material/Collections';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import RefreshIcon from '@mui/icons-material/Refresh';
import ShareIcon from '@mui/icons-material/Share';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';

import { DeviceType,SimulatePreviewResponse } from '../../types/previewStudio';
import QRCode from '../Creatives/PreviewQRCode';
import DeviceFrame from './DeviceFrame';

interface PreviewPanelProps {
  previewResponse: SimulatePreviewResponse | null;
  isGenerating: boolean;
  error: string | null;
  deviceType: DeviceType;
  selectedSize: string;
  iframeRef: React.MutableRefObject<HTMLIFrameElement | null>;
  expandableMode?: boolean;
  onShare?: () => void;
  onCreateGallery?: () => void;
  // QR code props
  qrCodeData?: {
    insertionOrderId: number;
    campaignId: number;
    creativeId: number;
    creativeName: string;
  } | null;
}

export default function PreviewPanel({
  previewResponse,
  isGenerating,
  error,
  deviceType,
  selectedSize,
  iframeRef,
  expandableMode = false,
  onShare,
  onCreateGallery,
  qrCodeData,
}: PreviewPanelProps) {
  const [previewBlobUrl, setPreviewBlobUrl] = React.useState<string | null>(null);

  const getAdDimensions = () => {
    if (selectedSize) {
      const [w, h] = selectedSize.split('x').map(Number);
      if (w && h) return { width: w, height: h };
    }
    
    if (previewResponse?.metadata?.size) {
      const sizeStr = previewResponse.metadata.size;
      // If it's "VIDEO_AD", treat it as a video and use default video size
      if (sizeStr === 'VIDEO_AD' || previewResponse.metadata.creative_type === 'video') {
        return { width: 320, height: 180 };
      }
      // If it's "NATIVE_AD", use mobile interstitial size
      if (sizeStr === 'NATIVE_AD') {
        return { width: 320, height: 480 };
      }
      const [w, h] = sizeStr.split('x').map(Number);
      if (w && h) return { width: w, height: h };
    }
    
    if (previewResponse?.metadata?.creative_type === 'video') {
      // Default to mobile video size for videos
      return { width: 320, height: 180 };
    }
    
    // Fallback to standard banner size
    return { width: 320, height: 480 };
  };

  const { width: adWidth, height: adHeight } = getAdDimensions();

  // Get device frame dimensions
  const getDeviceFrameDimensions = React.useCallback(() => {
    const isLeaderboard = adWidth === 728 && adHeight === 90;

    switch (deviceType) {
      case 'mobile':
        return { width: 375, height: 667 }; // iPhone 6/7/8
      case 'tablet':
        // Wider tablet frame for leaderboard ads
        if (isLeaderboard) {
          return { width: 850, height: 800 };
        }
        return { width: 768, height: 1024 }; // iPad
      case 'desktop':
        return { width: 1440, height: 900 }; // Desktop
      case 'ctv':
        return { width: 1920, height: 1080 }; // TV
      default:
        return { width: 375, height: 667 };
    }
  }, [adWidth, adHeight, deviceType]);

  // Track expanded state
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [expandedDimensions, setExpandedDimensions] = React.useState({ width: adWidth, height: adHeight });

  // Handle MRAID expand/close events from iframe
  // Listen unconditionally — Celtra and other MRAID creatives may expand
  // even before the user toggles expandableMode on
  React.useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only accept messages from our iframe
      if (event.source !== iframeRef.current?.contentWindow) return;

      const {data} = event;
      if (!data || !data.type) return;

      if (data.type === 'mraid_expand') {
        setIsExpanded(true);
        const expandWidth = data.width || 320;
        const expandHeight = data.height || 480;
        setExpandedDimensions({ width: expandWidth, height: expandHeight });
      } else if (data.type === 'mraid_close') {
        setIsExpanded(false);
        setExpandedDimensions({ width: adWidth, height: adHeight });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [adWidth, adHeight, iframeRef]);

  // Reset expanded state when preview changes
  React.useEffect(() => {
    setIsExpanded(false);
    setExpandedDimensions({ width: adWidth, height: adHeight });
  }, [previewResponse, adWidth, adHeight]);

  // Collapse when expandableMode is turned OFF
  React.useEffect(() => {
    if (!expandableMode && isExpanded) {
      setIsExpanded(false);
      setExpandedDimensions({ width: adWidth, height: adHeight });
    }
  }, [expandableMode, isExpanded, adWidth, adHeight]);

  // Extract adm for blob URL generation
  const adm = previewResponse?.adm;
  const isCeltraTag = typeof adm === 'string' && adm.includes('celtra.com');

  // Strip third-party monitoring/verification scripts that serve no purpose in preview
  // and can cause performance issues (frame-walking, heavy DOM inspection, etc.)
  const sanitizeAdmForPreview = (markup: string): string =>
    markup
      .replace(/<script[^>]*doubleverify\.com[^>]*>[\s\S]*?<\/script>/gi, '')
      .replace(/<noscript>[\s\S]*?doubleverify\.com[\s\S]*?<\/noscript>/gi, '');

  // Process adm to ensure clicks open in new tab instead of navigating within iframe
  // This fixes click-out for first-party/hosted creatives where Venice doesn't add target="_blank"
  const processAdmForClickout = (markup: string | undefined): string => {
    if (!markup) return '';
    // Add target="_blank" to anchor tags that don't already have a target attribute
    return markup.replace(/<a\s(?![^>]*\btarget=)/gi, '<a target="_blank" ');
  };

  // Generate blob URL for iframe (fixes Celtra timing issues)
  // Use stable dependencies to prevent unnecessary regeneration
  const creativeType = previewResponse?.metadata?.creative_type;
  const previewId = previewResponse?.preview?.id;
  const previewIframeUrl = previewResponse?.iframe_url;

  React.useEffect(() => {
    // Generate blob URL for banner, mraid, and moments (when moments has adm instead of iframe_url)
    const isMomentsWithAdm = creativeType === 'moments' && adm && !previewIframeUrl;
    if (!adm || (creativeType !== 'banner' && creativeType !== 'mraid' && !isMomentsWithAdm)) {
      setPreviewBlobUrl(null);
      return undefined;
    }

    const blobDeviceDims = getDeviceFrameDimensions();

    // Generate minimal HTML for Celtra
    const htmlContent = `<!DOCTYPE html>
<html style="margin: 0; padding: 0; width: ${adWidth}px; height: ${adHeight}px; overflow: hidden;">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=${adWidth}, initial-scale=1, maximum-scale=1, user-scalable=no">
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    html, body {
      margin: 0 !important;
      padding: 0 !important;
      width: ${adWidth}px !important;
      height: ${adHeight}px !important;
      overflow: hidden !important;
    }
    body {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      position: relative;
    }
    ${(() => {
      if (!isCeltraTag) return '';
      if (!expandableMode) return `
    /* Constrain Celtra ad containers to declared ad dimensions */
    div[class^="celtra-ad-v"] {
      width: ${adWidth}px !important;
      height: ${adHeight}px !important;
      overflow: hidden !important;
      position: relative !important;
    }`;
      return `
    div[class^="celtra-ad-v"] {
      position: relative !important;
    }`;
    })()}
  </style>
  ${''/* Celtra parent-spoofing removed: sandbox without allow-same-origin blocks cross-frame access natively */}
  ${expandableMode ? `
  <!-- MRAID Mock Script (only if expandable) -->
  <script>
    (function() {
      if (typeof window.mraid === 'undefined') {
        var mraidState = 'default';
        var eventListeners = {};
        
        function fireEvent(eventName, data) {
          if (eventListeners[eventName]) {
            eventListeners[eventName].forEach(function(listener) {
              try { listener(data); } catch (e) {}
            });
          }
        }
        
        var isReady = false;
        var isViewable = true;

        window.mraid = {
          VERSION: '2.0',
          getVersion: function() { return '2.0'; },
          getState: function() { return mraidState; },
          isViewable: function() { return isViewable; },
          addEventListener: function(event, listener) {
            if (!eventListeners[event]) eventListeners[event] = [];
            eventListeners[event].push(listener);
            // If ready already fired and listener is for ready, fire immediately
            if (event === 'ready' && isReady) {
              try { setTimeout(function() { listener(); }, 0); } catch (e) {}
            }
            // If viewableChange listener added and already viewable, notify
            if (event === 'viewableChange' && isViewable) {
              try { setTimeout(function() { listener(true); }, 0); } catch (e) {}
            }
          },
          removeEventListener: function(event, listener) {
            if (eventListeners[event]) {
              var idx = eventListeners[event].indexOf(listener);
              if (idx > -1) eventListeners[event].splice(idx, 1);
            }
          },
          expand: function(url) {
            if (mraidState === 'default') {
              window.parent.postMessage({
                type: 'mraid_expand',
                width: 320,
                height: 480
              }, '*');
              mraidState = 'expanded';
              fireEvent('stateChange', 'expanded');
            }
          },
          close: function() {
            if (mraidState === 'expanded') {
              window.parent.postMessage({ type: 'mraid_close' }, '*');
              mraidState = 'default';
              fireEvent('stateChange', 'default');
            }
          },
          open: function(url) {
            if (url && url.trim()) window.open(url, '_blank');
          },
          getPlacementType: function() { return 'inline'; },
          useCustomClose: function() {},
          setExpandProperties: function() {},
          getExpandProperties: function() {
            return { width: 320, height: 480, useCustomClose: false };
          },
          getScreenSize: function() {
            return { width: ${blobDeviceDims.width}, height: ${blobDeviceDims.height} };
          },
          getMaxSize: function() {
            return { width: ${blobDeviceDims.width}, height: ${blobDeviceDims.height} };
          },
          getCurrentPosition: function() {
            if (mraidState === 'expanded') {
              return { x: 0, y: 0, width: 320, height: 480 };
            }
            return { x: 0, y: 0, width: ${adWidth}, height: ${adHeight} };
          },
          getDefaultPosition: function() {
            return { x: 0, y: 0, width: ${adWidth}, height: ${adHeight} };
          },
          supports: function() { return true; }
        };

        // Fire ready after document is fully loaded (including dynamic scripts)
        function triggerReady() {
          if (!isReady) {
            isReady = true;
            fireEvent('ready');
            // Also fire viewableChange shortly after
            setTimeout(function() { fireEvent('viewableChange', true); }, 50);
          }
        }

        // Wait for window load to ensure all scripts (including dynamically added ones) have loaded
        if (document.readyState === 'complete') {
          setTimeout(triggerReady, 100);
        } else {
          window.addEventListener('load', function() { setTimeout(triggerReady, 100); });
        }
      }
    })();
  </script>
  ` : ''}
</head>
<body>
  ${sanitizeAdmForPreview(processAdmForClickout(adm))}
</body>
</html>`;

    // Create blob URL
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    setPreviewBlobUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [adm, adWidth, adHeight, expandableMode, deviceType, previewId, creativeType, previewIframeUrl, getDeviceFrameDimensions, isCeltraTag]);

  /**
   * Render preview content based on creative type
   */
  const renderPreviewContent = () => {
    if (!previewResponse) return null;
    const { metadata, adm: responseAdm, iframe_url: iframeUrl, vast_url: vastUrl } = previewResponse;

    const currentWidth = isExpanded ? expandedDimensions.width : adWidth;
    const currentHeight = isExpanded ? expandedDimensions.height : adHeight;

    // Check if this is a 3p/1p
    const isHostedCreativeWithoutTag = metadata.warnings?.some(
      (w: string) => w.toLowerCase().includes('no tag') || w.toLowerCase().includes('has no tag')
    );

    switch (metadata.creative_type) {
      case 'banner':
      case 'mraid':
        if (!responseAdm) {
          if (isHostedCreativeWithoutTag) {
            return (
              <Alert severity="info">
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Hosted image creative detected
                </Typography>
                <Typography variant="body2">
                  This creative uses a hosted image without an ad tag. Please use{' '}
                  <strong>Live Preview</strong> mode or <strong>Creative Sandbox</strong> to
                  preview this creative, as the ad markup is generated by the ad server at bid time.
                </Typography>
              </Alert>
            );
          }
          return (
            <Alert severity="warning">
              No ad markup (adm) available for this creative
            </Alert>
          );
        }

        return (
          <Box
            sx={{
              width: `${currentWidth}px`,
              height: `${currentHeight}px`,
              bgcolor: 'white',
              overflow: isExpanded ? 'visible' : 'hidden',
              position: 'relative',
              flexShrink: 0,
              transition: 'width 0.3s ease, height 0.3s ease',
            }}
          >
            <iframe
              key={`preview-${previewId || 'none'}`}
              ref={iframeRef}
              title="Creative Preview"
              src={previewBlobUrl || undefined}
              scrolling="no"
              style={{
                width: `${currentWidth}px`,
                height: `${currentHeight}px`,
                border: 'none',
                display: 'block',
                overflow: 'hidden',
                transition: 'width 0.3s ease, height 0.3s ease',
              }}
              sandbox={`allow-scripts${!isCeltraTag ? ' allow-same-origin' : ''} allow-modals allow-popups allow-forms${!isCeltraTag ? ' allow-top-navigation' : ''} allow-popups-to-escape-sandbox`}
            />

            {/* Close button overlay when expanded */}
            {isExpanded && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  zIndex: 9999,
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.9)',
                  },
                }}
                onClick={() => {
                  // Trigger close
                  if (iframeRef.current?.contentWindow) {
                    iframeRef.current.contentWindow.postMessage({ type: 'trigger_close' }, '*');
                  }
                  setIsExpanded(false);
                  setExpandedDimensions({ width: adWidth, height: adHeight });
                }}
              >
                ×
              </Box>
            )}
          </Box>
        );

      case 'moments':
        // Moments can either have iframe_url OR adm (HTML markup)
        // If iframe_url exists, use iframe; otherwise fall back to rendering adm like a banner
        if (iframeUrl) {
          return (
            <Box
              sx={{
                width: `${adWidth}px`,
                height: `${adHeight}px`,
                bgcolor: 'white',
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              <iframe
                ref={iframeRef}
                title="Moments Preview"
                src={iframeUrl}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
                sandbox="allow-scripts allow-same-origin allow-modals allow-popups allow-forms allow-top-navigation allow-popups-to-escape-sandbox"
              />
            </Box>
          );
        }

        // Fallback: render adm like a banner if available
        if (responseAdm) {
          return (
            <Box
              sx={{
                width: `${currentWidth}px`,
                height: `${currentHeight}px`,
                bgcolor: 'white',
                overflow: 'hidden',
                position: 'relative',
                flexShrink: 0,
              }}
            >
              <iframe
                key={`moments-adm-${previewId || 'none'}`}
                ref={iframeRef}
                title="Moments Preview"
                src={previewBlobUrl || undefined}
                scrolling="no"
                style={{
                  width: `${currentWidth}px`,
                  height: `${currentHeight}px`,
                  border: 'none',
                  display: 'block',
                  overflow: 'hidden',
                }}
                sandbox="allow-scripts allow-same-origin allow-modals allow-popups allow-forms allow-top-navigation allow-popups-to-escape-sandbox"
              />
            </Box>
          );
        }

        return (
          <Alert severity="warning">
            No iframe URL or ad markup available for this Moments creative
          </Alert>
        );

      case 'video': {
        // Check for inline video: video_type === 'inline' or size === 'VIDEO_AD' (both indicate inline video)
        const isInlineVideo = metadata.video_type === 'inline';
        const isVideoAdSize = metadata.size === 'VIDEO_AD';
        let videoUrl: string | null = null;
        
        // Try to extract video URL if we have adm data (for inline videos or VIDEO_AD size)
        if ((isInlineVideo || isVideoAdSize) && responseAdm) {
          try {
            const videoData = typeof responseAdm === 'string' ? JSON.parse(responseAdm) : responseAdm;
            
            // Extract video URL from the video_urls object
            // The keys are dimensions like "320x180", "768x432", etc.
            if (videoData.video_urls && typeof videoData.video_urls === 'object') {
              const videoUrls = videoData.video_urls;
              
              // Priority order: use the best quality available for the device
              // For mobile, prefer 320x180 or 480x270
              // For larger devices, prefer higher resolutions
              const preferredSizes = deviceType === 'mobile' 
                ? ['480x270', '320x180', '768x432', '1024x576']
                : ['1024x576', '768x432', '480x270', '320x180'];
              
              // Try preferred sizes in order
              const matchedSize = preferredSizes.find((size) => videoUrls[size]);
              if (matchedSize) {
                videoUrl = videoUrls[matchedSize];
              }
              
              // Fallback: use first available URL
              if (!videoUrl) {
                const firstKey = Object.keys(videoUrls)[0];
                videoUrl = videoUrls[firstKey];
              }
            } else if (videoData.raw_video_url) {
              videoUrl = videoData.raw_video_url;
            } else if (typeof videoData.video_urls === 'string') {
              videoUrl = videoData.video_urls;
            }
            
          } catch {
            // Failed to parse inline video data
          }
        }
        
        // Render inline video if we have a video URL and it's either explicitly inline or marked as VIDEO_AD
        if (videoUrl && (isInlineVideo || isVideoAdSize)) {
          return (
            <Box
              sx={{
                width: `${adWidth}px`,
                height: `${adHeight}px`,
                bgcolor: 'black',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <video
                src={videoUrl}
                controls
                autoPlay
                muted
                loop
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              >
                Your browser does not support the video tag.
              </video>
            </Box>
          );
        }
        
        // Fallback for VAST or non-inline video
        return (
          <Box
            sx={{
              width: `${adWidth}px`,
              height: `${adHeight}px`,
              bgcolor: 'black',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 2,
              p: 3,
              flexShrink: 0,
            }}
          >
            <Typography variant="h6" sx={{ color: 'white' }}>
              Video Preview
            </Typography>
            {vastUrl && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: 'grey.400', mb: 1 }}>
                  VAST URL:
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'grey.300',
                    fontFamily: 'monospace',
                    wordBreak: 'break-all',
                  }}
                >
                  {vastUrl}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <a
                    href={vastUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#64b5f6', textDecoration: 'underline' }}
                  >
                    Open video in new tab
                  </a>
                </Box>
              </Box>
            )}
          </Box>
        );
      }

      default:
        return (
          <Alert severity="info">
            Unknown creative type: {metadata.creative_type}
          </Alert>
        );
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        p: 2,
        position: 'relative',
        overflow: 'auto',
        bgcolor: '#f5f5f5',
      }}
    >
      {/* Empty State */}
      {!previewResponse && !isGenerating && !error && (
        <Box sx={{ textAlign: 'center', maxWidth: 400 }}>
          <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
            No Preview Generated
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Select a campaign and creative, then click &quot;Generate Preview&quot; to see your ad.
          </Typography>
        </Box>
      )}

      {/* Loading State */}
      {isGenerating && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <CircularProgress size={48} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Generating preview...
          </Typography>
        </Box>
      )}

      {/* Preview State */}
      {previewResponse && !isGenerating && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {/* Actions above device */}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              bgcolor: 'background.paper',
              borderRadius: 999,
              px: 1,
              py: 0.5,
              boxShadow: 1,
            }}
          >
            <Tooltip title="Reload preview">
              <IconButton
                size="small"
                onClick={() => {
                  if (iframeRef.current) {
                    // eslint-disable-next-line no-param-reassign, no-self-assign
                    iframeRef.current.src = iframeRef.current.src;
                  }
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Share with client">
              <IconButton
                size="small"
                onClick={onShare}
                disabled={!onShare}
              >
                <ShareIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Create preview gallery">
              <IconButton
                size="small"
                onClick={onCreateGallery}
                disabled={!onCreateGallery}
              >
                <CollectionsIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* QR Code for mobile preview */}
            {qrCodeData && (
              <QRCode
                url={`adops://ad_preview_qr?insertion_order_id=${qrCodeData.insertionOrderId}&campaign_id=${qrCodeData.campaignId}&creative_id=${qrCodeData.creativeId}`}
                size={24}
                opacity={1}
                title={qrCodeData.creativeName}
                renderAsTrigger={(onClick) => (
                  <Tooltip title="QR Code for mobile preview">
                    <IconButton size="small" onClick={onClick}>
                      <QrCode2Icon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              />
            )}
          </Box>

          {/* Device Frame */}
          <DeviceFrame
            deviceType={deviceType}
            adWidth={adWidth}
            adHeight={adHeight}
            expandableMode={expandableMode}
            isExpanded={isExpanded}
          >
            {renderPreviewContent()}
          </DeviceFrame>
        </Box>
      )}
    </Box>
  );
}
